import { useState, useEffect } from "react";
import { Sparkles, Loader2, ChevronRight, Target, TrendingUp } from "lucide-react";
import { matchCauses, type AICauseMatch } from "@/lib/ai-engine";
import { Link } from "@tanstack/react-router";
import { formatINR } from "@/data/sample";

export function AICauseMatcher({ preferences = [] }: { preferences?: string[] }) {
  const [causes, setCauses] = useState<AICauseMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    matchCauses(preferences, []).then((r) => {
      setCauses(r);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-2xl border border-[var(--maroon)]/20 overflow-hidden shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3 px-5 py-4 bg-[var(--maroon-deep)] text-white">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-semibold text-white text-sm">AI Cause Matcher</p>
          <p className="text-[11px] text-white/70">
            Personalised picks based on your giving history
          </p>
        </div>
        <span className="ml-auto rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-bold text-white uppercase tracking-wider">
          AI
        </span>
      </div>

      <div className="divide-y divide-border/40 bg-white">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4 animate-pulse">
                <div className="h-10 w-10 rounded-xl bg-border/40 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-border/40 rounded w-2/3" />
                  <div className="h-2 bg-border/30 rounded w-1/2" />
                </div>
              </div>
            ))
          : causes.map((c) => (
              <div
                key={c.causeId}
                className="group flex items-start gap-4 px-5 py-4 hover:bg-[var(--maroon)]/3 transition"
              >
                <div className="relative shrink-0">
                  <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9"
                      fill="none"
                      stroke="var(--border)"
                      strokeWidth="2.5"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="15.9"
                      fill="none"
                      stroke={
                        c.matchScore >= 90
                          ? "var(--saffron-deep)"
                          : c.matchScore >= 80
                            ? "var(--maroon)"
                            : "#94a3b8"
                      }
                      strokeWidth="2.5"
                      strokeDasharray={`${c.matchScore} ${100 - c.matchScore}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[var(--fg)]">
                    {c.matchScore}%
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--fg)] group-hover:text-[var(--maroon)] transition leading-snug">
                    {c.title}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground/60">{c.institution}</p>
                  <p className="mt-1.5 flex items-center gap-1 text-[11px] text-foreground/70">
                    <Target className="h-3 w-3 text-[var(--saffron-deep)]" />
                    {c.reason}
                  </p>
                </div>

                <Link
                  to="/donate"
                  search={{ cause: c.causeId, amount: c.suggestedAmount } as never}
                  className="shrink-0 flex items-center gap-1.5 rounded-xl bg-[var(--maroon)] px-3 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)] transition shadow-sm"
                >
                  {formatINR(c.suggestedAmount)}
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
      </div>

      <div className="flex items-center gap-1.5 border-t border-border/40 bg-[var(--maroon)]/5 px-5 py-2.5">
        <TrendingUp className="h-3 w-3 text-[var(--maroon)]" />
        <span className="text-[10px] text-foreground/60">
          Match scores refresh based on urgency and giving trends
        </span>
      </div>
    </div>
  );
}
